'use client';
import Button from 'components/generic-components/thera-start-components/Button';
import Highlight from 'components/generic-components/thera-start-components/Highlight';
import { useState } from 'react';
import { pricingFaqs } from 'lib/theraStartConstants';
import { GoArrowLeft } from 'react-icons/go';

const starterFeatures = ['Standard filing service', '100% accuracy guarantee', 'Worry-Free Compliance offer**'];

const proFeatures = [
  'Rush filing service',
  '100% accuracy guarantee',
  'Corporate Bylaws template',
  'Worry-Free Compliance offer**',
  '$1000 in Thera Credits',
];

export default function PricingStep({ formik, onBack, setStep }) {
  const [open, setOpen] = useState<number | null>(null);
  const selected = formik.values.package;

  const handlePlanSelect = (plan) => {
    formik.setFieldValue('package', plan);
    setStep(7);
  };

  return (
    <>
      {/* Custom back button */}
      <button
        type="button"
        onClick={onBack}
        className="mb-2 flex items-center gap-2 font-semibold text-white transition-colors hover:text-blue-500"
      >
        <GoArrowLeft size={18} />
        <span>Back</span>
      </button>

      <h2 className="mb-[2rem] text-2xl font-[600] text-white md:text-3xl">Choose your package</h2>
      <div className="mb-[2rem] flex w-full flex-col justify-center gap-[1.5rem] md:flex-row md:gap-[2rem]">
        {/* Starter Card */}
        <div
          className={`flex min-h-[32.5rem] w-full flex-col items-start rounded-xl border bg-white p-[1.5rem] shadow-lg transition-all duration-200 sm:p-[2.5rem] ${
            selected === 'starter' ? 'border-dark ring-2 ring-dark' : 'border-[#E5E5E5]'
          }`}
        >
          <div
            className="mb-[0.125rem] text-[1.25rem] font-bold text-[#232329] sm:text-[1.375rem]"
            style={{
              background: 'linear-gradient(205.36deg, #0B509F 13.3%, #041C32 65.3%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Starter
          </div>
          <div className="mb-2 text-[2rem] font-bold text-[#232329] sm:text-[2.25rem]">$0</div>
          <div className="mb-5 font-sans text-sm font-normal text-dark">
            Per Month <span className="text-xs text-[#757575]">+state fees</span>
          </div>
          <div className="mb-[0.1875rem] font-sans text-sm font-normal text-[#232329] sm:text-base">
            Covers all your required filings with the state, accuracy guaranteed.
          </div>
          <div className="">
            <span className="inline-block rounded-lg bg-[#F4F4F5] px-[0.1875rem] py-[0.0625rem] font-sans text-xs font-semibold tracking-wider text-[#232329] sm:px-[0.25rem]">
              PROCESS IN 7-10 BUSINESS DAYS*
            </span>
          </div>
          <p className="my-[1rem] text-dark">Includes:</p>
          <ul className="m-0 mb-[2rem] w-full list-none p-0">
            {starterFeatures.map((f, i) => (
              <li key={i} className="mb-[0.1875rem] flex items-center font-sans text-sm text-light-gray sm:text-base">
                <svg width="1.25rem" height="1.25rem" viewBox="0 0 20 20" fill="none" className="mr-[0.75rem]">
                  <circle cx="10" cy="10" r="10" fill="#EDEDED" />
                  <path
                    d="M6 10.5L9 13.5L14 8.5"
                    stroke="#191919"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {f}
              </li>
            ))}
          </ul>
          <Button
            variant="white"
            size="md"
            className={`mt-auto w-full cursor-pointer rounded-lg bg-[#191919] font-sans text-[0.92rem] font-[400] !text-white`}
            type="button"
            onClick={() => handlePlanSelect('starter')}
          >
            Incorporate
          </Button>
        </div>
        {/* Pro Card */}
        <div
          className={`flex min-h-[32.5rem] w-full flex-col items-start rounded-xl border bg-therablue p-[1.5rem] shadow-lg transition-all duration-200 sm:p-[2.5rem] ${
            selected === 'pro' ? 'border-dark ring-2 ring-dark' : 'border-[#232329]'
          }`}
        >
          <div className="mb-[0.125rem] font-sans text-[1.25rem] font-bold text-white sm:text-[1.375rem]">Pro</div>
          <div className="mb-2 flex items-center">
            <span className="mr-[0.15625rem] text-[1.75rem] font-bold text-white">
              <Highlight
                style={{
                  backgroundColor: 'transparent',
                }}
              >
                $250.00
              </Highlight>
            </span>
            <span className="font-sans text-base font-semibold text-white line-through sm:text-lg">$349.00</span>
          </div>
          <div className="mb-5 font-sans text-sm font-normal text-white">
            Per Year <span className="text-xs">+state fees</span>
          </div>
          <div className="mb-[0.5rem] mt-1 font-sans text-sm font-normal text-white sm:text-base">
            Covers all your required filings with the state, accuracy guaranteed.
          </div>
          <div className="">
            <span className="inline-block rounded-lg bg-white px-[0.1875rem] py-[0.0625rem] font-sans text-xs font-semibold tracking-wider text-black sm:px-[0.25rem]">
              PROCESS IN 7-10 BUSINESS DAYS*
            </span>
          </div>
          <p className="my-[1rem] text-white">Everything in Starter, plus:</p>
          <ul className="m-0 mb-[2rem] w-full list-none p-0">
            {proFeatures.map((f, i) => (
              <li key={i} className="mb-[0.1875rem] flex items-center font-sans text-sm text-[#ffffffcc] sm:text-base">
                <svg width="1.25rem" height="1.25rem" viewBox="0 0 20 20" fill="none" className="mr-[0.75rem]">
                  <circle cx="10" cy="10" r="10" fill="#ffffff" />
                  <path
                    d="M6 10.5L9 13.5L14 8.5"
                    stroke="#191919"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {f}
              </li>
            ))}
          </ul>
          <Button
            size="md"
            className={`mt-auto w-full cursor-pointer rounded-lg bg-white font-sans text-[0.92rem] font-[400] !text-[#191919]`}
            type="button"
            onClick={() => handlePlanSelect('pro')}
          >
            Incorporate
          </Button>
        </div>
      </div>
      <div className="mx-auto mb-[2.5rem] w-full text-left text-[0.87rem] text-[#A1A1AA]">
        *Processing times are based on receiving complete information. Our processing times do not include Secretary of
        State processing times, which can vary.
      </div>
      <h3 className="mb-[1rem] text-center font-sans text-[1.5rem] font-bold text-white">
        Frequently asked questions from clients
      </h3>
      <div className="mx-auto mb-[0.125rem] flex w-full flex-col gap-[0.1875rem]">
        {pricingFaqs.slice(0, 5).map((faq, i) => (
          <div
            key={i}
            className={`cursor-pointer font-sans transition-all duration-200 ${
              open === i ? 'border-[#39393C] bg-[#232329] shadow-lg' : 'border-[#232329] bg-dark'
            } rounded-xl border-[0.09375rem] ${open === i ? 'px-[1.25rem] py-[1.5rem]' : 'px-[1.25rem] py-[1.25rem]'}`}
            onClick={() => setOpen(open === i ? -1 : i)}
          >
            <div className="flex items-center justify-between">
              <span className="text-[1.125rem] font-semibold text-white">{faq.q}</span>
              <span className="ml-[0.25rem] w-[1.375rem] select-none text-center text-2xl font-normal text-[#A1A1AA]">
                {open === i ? '−' : '+'}
              </span>
            </div>
            {open === i && faq.a && (
              <div className="mt-[1rem] text-sm font-normal leading-relaxed text-[#EDEDED]">{faq.a}</div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
